import { useRef, useState, useEffect } from "react";
import {
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  useMediaQuery,
  Container,
  Box,
} from "@mui/material";
import { popoverClasses } from "@mui/material/Popover";

import AppLogo from "../AppLogo/AppLogo";
import MobileNavigation from "../MobileNavigation/MobileNavigation";
import { ReactComponent as SwitchBusinessIcon } from "../../assets/switch-account-icon.svg";
import { ReactComponent as LogOutIcon } from "../../assets/log-out-icon.svg";
import { ReactComponent as SupportIcon } from "../../assets/message-circle-question.svg";
import { ReactComponent as AddIcon } from "../../assets/add.svg";
import { ReactComponent as ArrowRight } from "../../assets/arrow-right.svg";

import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import {
  FavouritesIcon,
  PayrollIcon,
  BusinessIcon,
  ReportsIcon,
  FinanceIcon,
} from "./NavigationIcons";
import { ReactComponent as HexagonIcon } from "../../assets/hexagon.svg";
import { useLoginPopup } from "../../context/LoginPopupContext";
// import Banner from "../Banner/Banner";

import { getAppTheme } from "../../config/resolve-app-brand";
import { useTheme } from "../../context/ThemeContext";
import ThemeToggle from "../ThemeToggle/ThemeToggle";
import { getCookie, getHeaders } from "../../utils/api";
import envConfig from "../../config";
import { checkAndUpdateApiKeyExpiration } from "../Dashboard/Dashboard";
import { createSessionForExternal } from "../../utils/common";

export const isMobileBrowser = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

const MenuSection = ({
  icon = {},
  handleMenuClick = () => {},
  handleMenuClose = () => {},
  companyName = "",
  currentMenuItem = "",
  urlPrefix = "",
  anchorEl = () => {},
  setAnchorEl = () => {},
  //
  mainMenuTitle = "",
  sectionOne = [],
  sectionOneTitle = "",
  sectionOneExtra = [],
  //
  sectionTwo = [],
  sectionTwoTitle = "",
  sectionTwoExtra = [],
  //
  sectionThree = [],
  sectionThreeTitle = "",
  sectionThreeExtra = [],
  //
  isCompanyTile,
  sectionFour = [],
  sectionFourTitle = "",
  sectionFourExtra = [],
  manageToolbar = [],
}) => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const menuRef = useRef();
  const theme = getAppTheme(isDarkMode);

  const columnMinWidth = 155;

  const maxItems = Math.max(
    sectionOne?.length,
    sectionTwo?.length,
    sectionThree?.length,
    sectionFour?.length
  );

  const fillSection = (section) => {
    if (!section?.length) return section;
    const filledSection = [...section];
    while (filledSection.length < maxItems) {
      filledSection.push({ Label: "", Hyperlink: null });
    }
    return filledSection;
  };

  const filledSectionOne = fillSection(sectionOne);
  const filledSectionTwo = fillSection(sectionTwo);
  const filledSectionThree = fillSection(sectionThree);
  const filledSectionFour = fillSection(sectionFour);

  const handleOutsideClick = (event) => {
    if (event === anchorEl || anchorEl?.contains(event.target)) {
      return;
    }
    if (
      Boolean(anchorEl) &&
      menuRef.current &&
      !menuRef.current?.contains(event.target)
    ) {
      console.log("trigger2");
      handleMenuClose();
    }
  };

  useEffect(() => {
    document.body.addEventListener("click", handleOutsideClick);

    return () => {
      document.body.removeEventListener("click", handleOutsideClick);
    };
  }, [anchorEl]);

  if (
    !sectionOne?.length > 0 &&
    !sectionTwo?.length > 0 &&
    !sectionThree?.length > 0 &&
    !sectionFour?.length > 0 &&
    !sectionOneExtra?.length > 0 &&
    !sectionTwoExtra?.length > 0 &&
    !sectionThreeExtra?.length > 0 &&
    !sectionFourExtra?.length > 0 &&
    !manageToolbar?.length
  )
    return;

  return (
    <Box>
      <IconButton
        color="inherit"
        onClick={(event) => handleMenuClick(event, mainMenuTitle)}
        disableRipple
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          paddingLeft: isCompanyTile ? "32px" : "8px",
          borderRadius: 0,
          position: "relative",
          stroke: theme.palette.text.primary,
          transition: "0.1s",
          "&:after": {
            content: '""',
            position: "absolute",
            top: "50%",
            left: "13px",
            transform: "translateY(-50%)",
            width: "1px",
            height: isCompanyTile && "39px",
          },
          "&:hover": {
            color: theme.palette.primary.main,
            stroke: theme.palette.primary.main,
          },
        }}
        data-testid="main-navigation--IconButton-0">
        {!isCompanyTile && icon}
        <Typography
          variant="body1"
          sx={{
            marginTop: 1,
            fontSize: isCompanyTile ? "11px" : "13px",
            fontWeight: 400,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "100px",
            position: isCompanyTile ? "absolute" : "unset",
            top: "9.5px",
            zIndex: 1,
            textTransform: isCompanyTile ? "uppercase" : "none",
            transition: "0.1s",
            color: isCompanyTile
              ? `${theme.palette.primary.main} !important`
              : "inherit",
          }}
        >
          {isCompanyTile ? companyName : mainMenuTitle}
        </Typography>
        {isCompanyTile && (
          <Typography
            variant="body1"
            sx={{
              fontSize: "13px",
              fontWeight: 400,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100px",
              mt: "32px",
              color: "inherit",
              transition: "0.1s",
            }}
          >
            Manage
          </Typography>
        )}
      </IconButton>
      <Menu
        hideBackdrop={true}
        ref={menuRef}
        disableScrollLock={true}
        anchorEl={anchorEl}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        open={Boolean(anchorEl) && currentMenuItem === mainMenuTitle}
        onClose={handleMenuClose}
        sx={{
          ">div": {
            zIndex: 2,
          },
          ">div.MuiPaper-elevation": {
            position: "fixed !important",
          },
          [`&.${popoverClasses.root}`]: {
            position: "unset",
          },
          ul: {
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              flexGrow: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: sectionOne.length > 0 ? columnMinWidth : 0,
              }}
            >
              {filledSectionOne?.length > 0 && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: "500",
                    fontSize: "18px",
                    paddingLeft: 2,
                  }}
                >
                  {sectionOneTitle}
                </Typography>
              )}
              {filledSectionOne?.map((page, pageIndex) => (
                <MenuItem
                  disableRipple
                  sx={{
                    "&:hover": {
                      bgcolor: "unset",
                    },
                    color: theme.palette.text.menuItemText,
                    cursor: page.Hyperlink ? "pointer" : "default",
                    fontFeatureSettings: "clig off, liga off",
                    height: "33px",
                  }}
                  key={pageIndex}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (page.Hyperlink) {
                      if (page.Hyperlink?.startsWith("https")) {
                        createSessionForExternal(page.Hyperlink);
                      } else {
                        navigate(`${page.Hyperlink}`);
                      }
                    }
                  }}
                  data-testid="main-navigation--MenuItem-0">
                  <a
                    onClick={(e) => e.preventDefault()}
                    style={{ textDecoration: "none", color: "inherit" }}
                    href={
                      page.Hyperlink?.startsWith("https")
                        ? `${page.Hyperlink}`
                        : `${page.Hyperlink}`
                    }
                    data-testid="main-navigation--a-0">
                    {page?.Label}
                  </a>
                </MenuItem>
              ))}
              {sectionOneExtra?.map((extra, extraIndex) => (
                <MenuItem
                  disableRipple
                  key={extraIndex}
                  sx={{
                    "&:hover": {
                      bgcolor: "unset",
                    },
                    color: theme.palette.text.secondary,
                    transition: ".1s",
                    fontWeight: "500",
                    paddingTop: extraIndex === 0 ? "24px" : "6px",
                    marginLeft: "16px",
                    marginRight: "16px",
                    paddingLeft: 0,
                    paddingRight: 0,
                    borderTop:
                      extraIndex === 0
                        ? isDarkMode
                          ? "rgba(255, 255, 255, 0.32) 1px solid"
                          : "rgba(0, 0, 0, 0.12) 1px solid"
                        : "none",
                    marginTop:
                      extra.type === "add_new_pay_event"
                        ? "53px"
                        : extraIndex === 0
                        ? "20px"
                        : "8px",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (extra.Label === "Add new Subscriber") {
                      navigate("/create-new-subscriber");
                      return;
                    }
                    if (extra.Hyperlink) {
                      if (extra.Hyperlink?.startsWith("https")) {
                        createSessionForExternal(extra.Hyperlink);
                      } else {
                        navigate(`${extra.Hyperlink}`);
                      }
                    }
                  }}
                  data-testid="main-navigation--MenuItem-1">
                  {extra.Icon === "plus" && (
                    <AddIcon
                      width={10}
                      height={10}
                      style={{
                        marginLeft: "5px",
                        marginRight: "12px",
                      }}
                    />
                  )}
                  <Link
                    onClick={(e) => e.preventDefault()}
                    style={{ textDecoration: "none", color: "inherit" }}
                    to={`${extra.Hyperlink}`}
                    data-testid="main-navigation--Link-0">
                    {extra?.Label}
                  </Link>
                  {extra.arrow_right && (
                    <ArrowRight
                      style={{
                        marginLeft: "15px",
                      }}
                    />
                  )}
                </MenuItem>
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: sectionTwo.length > 0 ? columnMinWidth : 0,
              }}
            >
              {/*
               */}
              {filledSectionTwo?.length > 0 && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: "500",
                    fontSize: "18px",
                    paddingLeft: 2,
                  }}
                >
                  {sectionTwoTitle}
                </Typography>
              )}
              {filledSectionTwo?.map((page, pageIndex) => (
                <MenuItem
                  disableRipple
                  sx={{
                    "&:hover": {
                      bgcolor: "unset",
                    },
                    color: theme.palette.text.menuItemText,
                    cursor: page.Hyperlink ? "pointer" : "default",
                    fontFeatureSettings: "clig off, liga off",
                    height: "33px",
                  }}
                  key={pageIndex}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (page.Hyperlink) {
                      if (page.Hyperlink?.startsWith("https")) {
                        createSessionForExternal(page.Hyperlink);
                      } else {
                        navigate(`${page.Hyperlink}`);
                      }
                    }
                  }}
                  data-testid="main-navigation--MenuItem-2">
                  <a
                    onClick={(e) => e.preventDefault()}
                    style={{ textDecoration: "none", color: "inherit" }}
                    href={
                      page.Hyperlink?.startsWith("https")
                        ? `${page.Hyperlink}`
                        : `${page.Hyperlink}`
                    }
                    data-testid="main-navigation--a-1">
                    {page?.Label}
                  </a>
                </MenuItem>
              ))}
              {sectionTwoExtra.map((extra, extraIndex) => (
                <MenuItem
                  disableRipple
                  key={extraIndex}
                  sx={{
                    "&:hover": {
                      bgcolor: "unset",
                    },
                    color: theme.palette.text.secondary,
                    fontWeight: "500",
                    paddingTop: extraIndex === 0 ? "24px" : "6px",
                    marginLeft: "16px",
                    marginRight: "16px",
                    paddingLeft: 0,
                    paddingRight: 0,
                    borderTop:
                      extraIndex === 0
                        ? isDarkMode
                          ? "rgba(255, 255, 255, 0.32) 1px solid"
                          : "rgba(0, 0, 0, 0.12) 1px solid"
                        : "none",
                    marginTop:
                      extra.type === "add_new_pay_event"
                        ? "53px"
                        : extraIndex === 0
                        ? "20px"
                        : "8px",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (extra.Hyperlink) {
                      if (extra.Hyperlink?.startsWith("https")) {
                        createSessionForExternal(extra.Hyperlink);
                      } else {
                        navigate(`${extra.Hyperlink}`);
                      }
                    }
                  }}
                  data-testid="main-navigation--MenuItem-3">
                  {extra.Icon === "plus" && (
                    <AddIcon
                      width={10}
                      height={10}
                      style={{
                        marginLeft: "5px",
                        marginRight: "12px",
                      }}
                    />
                  )}
                  <Link
                    style={{ textDecoration: "none", color: "inherit" }}
                    to={
                      extra.Hyperlink?.startsWith("https")
                        ? ""
                        : `${extra.Hyperlink}`
                    }
                    data-testid="main-navigation--Link-1">
                    {extra?.Label}
                  </Link>
                  {extra.arrow_right && (
                    <ArrowRight
                      style={{
                        marginLeft: "15px",
                      }}
                    />
                  )}
                </MenuItem>
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: sectionThree.length > 0 ? columnMinWidth : 0,
              }}
            >
              {filledSectionThree?.length > 0 && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: "500",
                    fontSize: "18px",
                    paddingLeft: 2,
                  }}
                >
                  {sectionThreeTitle}
                </Typography>
              )}
              {filledSectionThree?.map((page, pageIndex) => (
                <MenuItem
                  disableRipple
                  sx={{
                    "&:hover": {
                      bgcolor: "unset",
                    },
                    color: theme.palette.text.menuItemText,
                    cursor: page.Hyperlink ? "pointer" : "default",
                    fontFeatureSettings: "clig off, liga off",
                    height: "33px",
                  }}
                  key={pageIndex}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (page.Hyperlink) {
                      if (page.Hyperlink?.startsWith("https")) {
                        createSessionForExternal(page.Hyperlink);
                      } else {
                        navigate(`${page.Hyperlink}`);
                      }
                    }
                  }}
                  data-testid="main-navigation--MenuItem-4">
                  <a
                    onClick={(e) => e.preventDefault()}
                    style={{ textDecoration: "none", color: "inherit" }}
                    href={
                      page.Hyperlink?.startsWith("https")
                        ? `${page.Hyperlink}`
                        : `${page.Hyperlink}`
                    }
                    data-testid="main-navigation--a-2">
                    {page?.Label}
                  </a>
                </MenuItem>
              ))}
              {sectionThreeExtra.map((extra, extraIndex) => (
                <MenuItem
                  disableRipple
                  key={extraIndex}
                  sx={{
                    "&:hover": {
                      bgcolor: "unset",
                    },
                    color: theme.palette.text.secondary,
                    fontWeight: "500",
                    paddingTop: extraIndex === 0 ? "24px" : "6px",
                    marginLeft: "16px",
                    marginRight: "16px",
                    paddingLeft: 0,
                    paddingRight: 0,
                    borderTop:
                      extraIndex === 0
                        ? isDarkMode
                          ? "rgba(255, 255, 255, 0.32) 1px solid"
                          : "rgba(0, 0, 0, 0.12) 1px solid"
                        : "none",
                    marginTop:
                      extra.type === "add_new_pay_event"
                        ? "53px"
                        : extraIndex === 0
                        ? "20px"
                        : "8px",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (extra.Hyperlink) {
                      if (extra.Hyperlink?.startsWith("https")) {
                        createSessionForExternal(extra.Hyperlink);
                      } else {
                        navigate(`${extra.Hyperlink}`);
                      }
                    }
                  }}
                  data-testid="main-navigation--MenuItem-5">
                  {extra.Icon === "plus" && (
                    <AddIcon
                      width={10}
                      height={10}
                      style={{
                        marginLeft: "5px",
                        marginRight: "12px",
                      }}
                    />
                  )}
                  <Link
                    style={{ textDecoration: "none", color: "inherit" }}
                    to={
                      extra.Hyperlink?.startsWith("https")
                        ? ""
                        : `${extra.Hyperlink}`
                    }
                    data-testid="main-navigation--Link-2">
                    {extra?.Label}
                  </Link>
                  {extra.arrow_right && (
                    <ArrowRight
                      style={{
                        marginLeft: "15px",
                      }}
                    />
                  )}
                </MenuItem>
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                minWidth: sectionFour.length > 0 ? columnMinWidth : 0,
              }}
            >
              {filledSectionFour.length > 0 && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: "500",
                    fontSize: "18px",
                    paddingLeft: 2,
                  }}
                >
                  {sectionFourTitle}
                </Typography>
              )}
              {filledSectionFour?.map((page, pageIndex) => (
                <MenuItem
                  disableRipple
                  sx={{
                    "&:hover": {
                      bgcolor: "unset",
                    },
                    color: theme.palette.text.menuItemText,
                    cursor: page.Hyperlink ? "pointer" : "default",
                    fontFeatureSettings: "clig off, liga off",
                    height: "33px",
                  }}
                  key={pageIndex}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (page.Hyperlink) {
                      if (page.Hyperlink?.startsWith("https")) {
                        createSessionForExternal(page.Hyperlink);
                      } else {
                        navigate(page.Hyperlink);
                      }
                    }
                  }}
                  data-testid="main-navigation--MenuItem-6">
                  <a
                    onClick={(e) => e.preventDefault()}
                    style={{ textDecoration: "none", color: "inherit" }}
                    href={
                      page.Hyperlink?.startsWith("https")
                        ? `${page.Hyperlink}`
                        : `${page.Hyperlink}`
                    }
                    data-testid="main-navigation--a-3">
                    {page?.Label}
                  </a>
                </MenuItem>
              ))}
              {sectionFourExtra.map((extra, extraIndex) => (
                <MenuItem
                  disableRipple
                  key={extraIndex}
                  sx={{
                    color: theme.palette.text.secondary,
                    "&:hover": {
                      bgcolor: "unset",
                    },
                    fontWeight: "500",
                    paddingTop: extraIndex === 0 ? "24px" : "6px",
                    marginLeft: "16px",
                    marginRight: "16px",
                    paddingLeft: 0,
                    paddingRight: 0,
                    borderTop:
                      extraIndex === 0
                        ? isDarkMode
                          ? "rgba(255, 255, 255, 0.32) 1px solid"
                          : "rgba(0, 0, 0, 0.12) 1px solid"
                        : "none",
                    marginTop:
                      extra.type === "add_new_pay_event"
                        ? "53px"
                        : extraIndex === 0
                        ? "20px"
                        : "8px",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (extra.Hyperlink) {
                      if (extra.Hyperlink?.startsWith("https")) {
                        createSessionForExternal(extra.Hyperlink);
                      } else {
                        navigate(extra.Hyperlink);
                      }
                    }
                    if (extra.type === "dark_mode") {
                      handleMenuClose();
                    }
                  }}
                  data-testid="main-navigation--MenuItem-7">
                  {extra.type === "dark_mode" && <ThemeToggle />}
                  {extra.type === "switch_business" && (
                    <SwitchBusinessIcon
                      width={20}
                      height={20}
                      style={{ marginRight: "8px" }}
                    />
                  )}
                  {extra.type === "log_out" && (
                    <LogOutIcon
                      width={20}
                      height={20}
                      style={{ marginRight: "8px" }}
                    />
                  )}
                  {extra.Icon === "plus" && (
                    <AddIcon
                      width={10}
                      height={10}
                      style={{
                        marginLeft: "5px",
                        marginRight: "12px",
                      }}
                    />
                  )}
                  <Link
                    style={{ textDecoration: "none", color: "inherit" }}
                    to={
                      extra.type === "switch_business"
                        ? "/user-dashboard"
                        : extra.Hyperlink?.startsWith("https")
                        ? ""
                        : `${extra.Hyperlink}`
                    }
                    data-testid="main-navigation--Link-3">
                    {extra?.Label}
                  </Link>
                  {extra.arrow_right && (
                    <ArrowRight
                      style={{
                        marginLeft: "15px",
                      }}
                    />
                  )}
                </MenuItem>
              ))}
            </Box>
          </div>
          {manageToolbar?.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                mt: 2,
                pt: 2,
                borderTop: isDarkMode
                  ? "rgba(255, 255, 255, 0.32) 1px solid"
                  : "rgba(0, 0, 0, 0.12) 1px solid",
                justifyContent: "flex-center",
              }}
            >
              {manageToolbar.map((extra, extraIndex) => (
                <MenuItem
                  disableRipple
                  key={extraIndex}
                  sx={{
                    color: theme.palette.text.secondary,
                    "&:hover": {
                      bgcolor: "unset",
                    },
                    fontSize: 13,
                    fontWeight: "bold",
                    marginLeft: 1,
                    marginRight: 1,
                    paddingLeft: 0,
                    paddingRight: 0,
                    marginTop: 0,
                    pt: 1,
                    li: {
                      mt: 0,
                      justifyContent: "center",
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (extra.Hyperlink) {
                      if (extra.Hyperlink?.startsWith("https://help")) {
                        window.open(extra.Hyperlink);
                      }
                      if (extra.Hyperlink?.startsWith("https")) {
                        createSessionForExternal(extra.Hyperlink);
                      } else {
                        navigate(extra.Hyperlink);
                      }
                    }
                    if (extra.type === "dark_mode") {
                      handleMenuClose();
                    }
                  }}
                  data-testid="main-navigation--MenuItem-8">
                  {extra.type === "support" && (
                    <Box
                      sx={{
                        display: "flex",
                        height: "20px",
                        mr: 1,
                        "&:hover": {
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      <SupportIcon width={20} height={20} />
                    </Box>
                  )}
                  {extra.type === "dark_mode" && (
                    <Box
                      sx={{
                        li: { mr: 0 },
                        label: { mr: 0 },
                      }}
                    >
                      <ThemeToggle title="" />
                    </Box>
                  )}
                  {extra.type === "switch_business" && (
                    <SwitchBusinessIcon
                      width={20}
                      height={20}
                      style={{ marginRight: "8px" }}
                    />
                  )}
                  {extra.type === "log_out" && (
                    <LogOutIcon
                      width={20}
                      height={20}
                      style={{ marginRight: "8px" }}
                    />
                  )}
                  {extra.Icon === "plus" && (
                    <AddIcon
                      width={10}
                      height={10}
                      style={{
                        marginLeft: "5px",
                        marginRight: "12px",
                      }}
                    />
                  )}
                  <Link
                    style={{ textDecoration: "none", color: "inherit" }}
                    to={
                      extra.type === "switch_business"
                        ? `/user-dashboard`
                        : extra.Hyperlink?.startsWith("https")
                        ? ""
                        : `${extra.Hyperlink}`
                    }
                    data-testid="main-navigation--Link-4">
                    {extra?.Label}
                  </Link>
                  {extra.arrow_right && (
                    <ArrowRight
                      style={{
                        marginLeft: "15px",
                      }}
                    />
                  )}
                </MenuItem>
              ))}
            </Box>
          )}
        </Box>

        {/* ),
                )} */}
      </Menu>
    </Box>
  );
};

const MainNavigation = ({ pageCode }) => {
  const databaseIdFromCookies = getCookie("databaseId");
  const { dbId, lang } = useParams();
  const urlPrefix = `/${dbId || databaseIdFromCookies}/${lang || "en-au"}/`;

  const navigate = useNavigate();
  const { handleOpen } = useLoginPopup();
  const { isDarkMode } = useTheme();
  const theme = getAppTheme(isDarkMode);
  const isMobile = useMediaQuery("(max-width: 805px)");
  const location = useLocation();

  const [anchorEl, setAnchorEl] = useState(null);
  const [currentMenuItem, setCurrentMenuItem] = useState(null);
  const [newMenuItems, setNewMenuItems] = useState({});
  const [pageTitle, setPageTitle] = useState("");

  useEffect(() => {
    setAnchorEl(null);
    setCurrentMenuItem(null);
  }, [location]);

  const handleMenuClick = (event, menuItem) => {
    setAnchorEl(event.currentTarget);
    setCurrentMenuItem(menuItem);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentMenuItem(null);
  };

  useEffect(() => {
    let isMounted = true;
    let callCount = 0;
    const maxCalls = 3;

    const fetchData = async () => {
      const accessLoaded = getCookie("accessKey");
      const refreshLoaded = getCookie("refreshKey");

      if (
        `${accessLoaded}` === "undefined" ||
        !accessLoaded ||
        `${refreshLoaded}` === "undefined" ||
        !refreshLoaded
      ) {
        if (callCount < maxCalls) {
          callCount++;
          setTimeout(async () => {
            await fetchData();
          }, 1000);
        } else {
          console.log("Maximum number of calls reached.");
          callCount = 0;
          handleOpen();
          return;
        }
      }
      if (!accessLoaded || !refreshLoaded) {
        return;
      }
      try {
        const apiUrl = `https://${envConfig.apiDev2}/api/front-end/get-page-main-data?BaseHostURL=${envConfig.loginUrl}`;
        const headers = getHeaders();

        const LinkA = localStorage?.getItem("savedReferrer");
        const indexOfCom = LinkA?.indexOf(".com");
        const indexOfAu = LinkA?.indexOf(".au");

        const substringEndIndex =
          indexOfCom !== -1
            ? indexOfCom + 4
            : indexOfAu !== -1
            ? indexOfAu + 3
            : LinkA.length;

        const finalLinkA = LinkA?.substring(0, substringEndIndex);

        const response = await fetch(apiUrl, {
          method: "POST",
          headers: headers,
          body: JSON.stringify({
            Tool0004: "string",
            PageCode: pageCode,
            LinkS: `https://${envConfig.mainServiceUrl}`,
            LinkA:
              !LinkA ||
              LinkA == "undefined" ||
              LinkA?.includes("my.") ||
              LinkA?.includes("s8.") ||
              LinkA?.includes("s1v.")
                ? `https://${envConfig.correspondingService}`
                : finalLinkA,
            Lang: "en-au",
            BrowserType: isMobileBrowser() ? "M" : "D",
          }),
        });

        if (response.ok) {
          const responseData = await response.json();

          if (responseData.SuccessYN === "Y" && isMounted) {
            checkAndUpdateApiKeyExpiration();
            setPageTitle(responseData.PageTitle);
            setNewMenuItems(responseData);
            localStorage.setItem("businessCode", responseData?.BusinessCode);
          } else {
            handleOpen();
            console.error("Error: ", responseData.ErrorMessage);
          }
        } else {
          console.log("3");
          handleOpen();
          console.error("Error: Unable to fetch data");
        }
      } catch (error) {
        console.log("4");
        console.error("Error: ", error);
      }
    };

    fetchData();

    return () => {
      isMounted = false; // Set isMounted to false when the component unmounts
    };
  }, [pageCode]);

  useEffect(() => {
    document.title = pageTitle ? pageTitle : "Exacc";
  }, [pageTitle]);

  return (
    <>
      <Container
        key={newMenuItems?.BusinessCode}
        style={{
          display: "flex",
          justifyContent: "space-between",
          // borderBottom: "1px solid #EEE",
          marginBottom: "32px",
          padding: 0,
        }}
      >
        <AppLogo
          height={42}
          width={38}
          onClick={() => {
            navigate("/");
          }}
          style={{
            color: theme.palette.primary.main,
            cursor: "pointer",
            marginTop: 20,
            marginBottom: 20,
          }}
        />
        <Toolbar sx={{ display: "flex", maxWidth: "724px", pr: 0 }}>
          {isMobile ? (
            <MobileNavigation
              newMenuItems={newMenuItems}
              urlPrefix={urlPrefix}
            />
          ) : (
            // menuItems.map(
            //   (menuItem, index) =>
            //     (companyName !== "My Profile" || menuItem.type === "company") && (
            (<div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-end",
              }}
            >
              <MenuSection
                icon={<FavouritesIcon width={24} height={24} />}
                handleMenuClick={handleMenuClick}
                handleMenuClose={handleMenuClose}
                companyName={newMenuItems?.BusinessCode}
                currentMenuItem={currentMenuItem}
                urlPrefix={urlPrefix}
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                //
                mainMenuTitle="Favorites"
                sectionOneTitle="Favorites"
                sectionOne={newMenuItems?.Me_QuickLinks}
              />
              <MenuSection
                icon={<PayrollIcon width={24} height={24} />}
                handleMenuClick={handleMenuClick}
                handleMenuClose={handleMenuClose}
                companyName={newMenuItems?.BusinessCode}
                currentMenuItem={currentMenuItem}
                urlPrefix={urlPrefix}
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                //
                mainMenuTitle="Payroll"
                sectionOne={newMenuItems?.Payroll_Payroll_Links}
                sectionOneTitle="Payroll"
                sectionOneExtra={newMenuItems?.Payroll_Payroll_Extras}
                sectionTwo={newMenuItems?.Payroll_Rosters_Links}
                sectionTwoTitle="Rosters"
                sectionTwoExtra={newMenuItems?.Payroll_Rosters_Extras}
                sectionThree={newMenuItems?.Payroll_Timesheet_Links}
                sectionThreeTitle="Timesheet"
                sectionThreeExtra={newMenuItems?.Payroll_Timesheet_Extras}
              />
              <MenuSection
                icon={<BusinessIcon width={23} height={24} />}
                handleMenuClick={handleMenuClick}
                handleMenuClose={handleMenuClose}
                companyName={newMenuItems?.BusinessCode}
                currentMenuItem={currentMenuItem}
                urlPrefix={urlPrefix}
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                //
                mainMenuTitle="Business"
                sectionOne={newMenuItems?.Contacts_Links}
                sectionOneTitle="Contacts"
                sectionOneExtra={newMenuItems?.Contacts_Extras}
                sectionTwo={newMenuItems?.Invoices_Purchases_Links}
                sectionTwoTitle="Purchases"
                sectionTwoExtra={newMenuItems?.Invoices_Purchases_Extras}
                sectionThree={newMenuItems?.Invoices_Sales_Links}
                sectionThreeTitle="Sales"
                sectionThreeExtra={newMenuItems?.Invoices_Sales_Extras}
              />
              <MenuSection
                icon={
                  <FinanceIcon
                    style={{ marginBottom: "-3px" }}
                    width={30}
                    height={30}
                  />
                }
                handleMenuClick={handleMenuClick}
                handleMenuClose={handleMenuClose}
                companyName={newMenuItems?.BusinessCode}
                currentMenuItem={currentMenuItem}
                urlPrefix={urlPrefix}
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                //
                mainMenuTitle="Finance"
                sectionOneTitle="Accounting"
                sectionOne={newMenuItems?.Accounting_Links}
                sectionOneExtra={newMenuItems?.Accounting_Extras}
                sectionTwoTitle="Banking"
                sectionTwo={newMenuItems?.Accounting_Banking_Links}
                sectionTwoExtra={newMenuItems?.Accounting_Banking_Extras}
              />
              <MenuSection
                icon={<ReportsIcon width={28} height={28} />}
                handleMenuClick={handleMenuClick}
                handleMenuClose={handleMenuClose}
                companyName={newMenuItems?.BusinessCode}
                currentMenuItem={currentMenuItem}
                urlPrefix={urlPrefix}
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                //
                mainMenuTitle="Reports"
                sectionOne={newMenuItems?.Reports}
              />
              <MenuSection
                icon={
                  <HexagonIcon
                    style={{ marginBottom: "-1px" }}
                    width={27}
                    height={27}
                  />
                }
                handleMenuClick={handleMenuClick}
                handleMenuClose={handleMenuClose}
                companyName={newMenuItems?.BusinessCode || "Exacc"}
                currentMenuItem={currentMenuItem}
                urlPrefix={urlPrefix}
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                isCompanyTile={true}
                sectionOne={
                  newMenuItems?.Me_Configuration?.length
                    ? newMenuItems?.Me_Configuration
                    : newMenuItems.Subscribers
                }
                sectionOneTitle={
                  newMenuItems?.Me_Configuration?.length
                    ? "Configuration"
                    : "Subscribers"
                }
                sectionOneExtra={
                  newMenuItems?.Me_Configuration?.length
                    ? newMenuItems?.Me_Configuration_Extras
                    : newMenuItems?.Subscribers_Extra
                }
                sectionTwo={newMenuItems?.Me_MyProfile}
                sectionTwoTitle="My Profile"
                sectionFour={
                  newMenuItems?.Me_Users?.length
                    ? newMenuItems?.Me_Users
                    : newMenuItems?.GlobalProfile
                }
                sectionFourTitle={
                  newMenuItems?.Me_Users?.length ? "Users" : "My Profile"
                }
                sectionFourExtra={newMenuItems?.Users_Extras}
                manageToolbar={[
                  {
                    type: "support",
                    Hyperlink: newMenuItems?.HelpUrl,
                  },
                  {
                    type: "dark_mode",
                  },
                  {
                    name: "Switch",
                    type: "switch_business",
                    Hyperlink: "/user-dashboard",
                    Label: "Switch",
                  },
                  {
                    name: "Log Out",
                    type: "log_out",
                    Hyperlink: `/logout?apiKey=${getCookie(
                      "apiKey"
                    )}-${getCookie("databaseId")}&sessionId=${getCookie(
                      "sessionId"
                    )}`,
                    Label: "Log out",
                  },
                ]}
              />
            </div>)
            //       ),
            //   )
          )}
        </Toolbar>
      </Container>
      {/* {newMenuItems?.BannerHtml?.severity && (
        <Banner
          ip={ip}
          severity={newMenuItems?.BannerHtml?.severity}
          title={newMenuItems?.BannerHtml?.title}
          description={newMenuItems?.BannerHtml?.description}
          backgroundColor={newMenuItems?.BannerHtml?.backgroundColor}
          linkText={newMenuItems?.BannerHtml?.linkText}
          linkUrl={newMenuItems?.BannerHtml?.linkUrl}
        />
      )} */}
    </>
  );
};

export default MainNavigation;
